import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanPageHeader } from "../components/core/KonanPageHeader"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import type { FilterOptions } from "../components/filters/MenuFilter"
import { KonanEmptyState, KonanEmptyStateFilter } from "../components/utility/KonanEmptyState"
import { ScriptCard, ScriptLoadingCard } from "../features/Scripts"
import { StatusTextMap } from "../features/Scripts/Interfaces"
import { ScriptDialog } from "../features/Scripts/ScriptDialog"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { PaginatedScriptGroupListList } from "../types/generated/api/PaginatedScriptGroupListList"
import type { ScriptGroupList } from "../types/generated/api/ScriptGroupList"
import { useIsPermitted } from "../utils/PermissionsHelpers"
import { extractPageFromBackEndPaginationLink } from "../utils/genericHelpers"

/**
 * A component that manages the display of scripts using an infinite scroll implementation.
 * It allows the user to filter, sort, and search through scripts. The component provides
 * interactions to create new scripts or use existing templates through dialog components.
 *
 * The component handles various states for loading and fetching scripts, as well as dialog
 * states for creating and managing scripts. It fetches scripts dynamically as the user scrolls,
 * making use of the `useInfiniteQuery` from react-query for data fetching and pagination.
 *
 * @returns {React.ReactElement} The Scripts component that includes the search, filter, sorting UI,
 * and an infinite scrolling list of ScriptCard components. It dynamically opens dialogs for script
 * creation and template usage based on user interactions.
 */
export function Scripts(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("scripts")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve script", permissions.konan, flattenedKonanPermissions)
  const canAddScript = useIsPermitted("Add script", permissions.konan, flattenedKonanPermissions)

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastScriptRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isScriptsLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // SSF states
  const [searchValue, setSearchValue] = useState<string>("")
  const [filterValue, setFilterValue] = useState<FilterOptions>({
    "Script status": [],
  })
  const [sortValue, setSortValue] = useState<SortKey>("Most Recent")

  // SSF Types And Constants
  type SortKey = keyof typeof SORT_OPTIONS
  const SORT_OPTIONS = {
    "Most Recent": "-created_at",
    Name: "name",
  } as const

  // Open dialogs states
  const [isScriptDialogOpen, setIsScriptDialogOpen] = useState<boolean>(false)

  // function that gets the StatusTextMap enum key for a given display string value
  function getStatusKeyByValue(value: string): keyof typeof StatusTextMap {
    return Object.keys(StatusTextMap).find(
      (key) => StatusTextMap[key as keyof typeof StatusTextMap] === value,
    ) as keyof typeof StatusTextMap
  }

  const {
    data: scripts,
    isLoading: isScriptsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScriptGroupListList>, AxiosError, ScriptGroupList>(
    ["scripts", projectId, searchValue, filterValue, sortValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchScripts(
        projectId as string,
        pageParam,
        20,
        searchValue,
        // Turns script type filters into a boolean (true for built-in, false for custom) or undefined for no/all filter(s).
        false,
        // Converts selected script status labels e.g. "Pending" to enum keys e.g. "PENDING" so it matches backend format
        // Also Fixes inconsistency between FE labels and BE expected values  e.g. ( FE: "Building" and BE: "RUNNING")
        filterValue["Script status"]?.map((status) => getStatusKeyByValue(status)),
        SORT_OPTIONS[sortValue],
      ),

    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId,
      refetchOnMount: true,
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce(
          (accumulator, page) => accumulator.concat(page.data.results as Array<ScriptGroupList>),
          [] as Array<ScriptGroupList>,
        ),
      }),
    },
  )

  return (
    <Fragment>
      {isScriptDialogOpen && (
        <ScriptDialog isOpen onClose={() => setIsScriptDialogOpen(false)} isNewScriptTriggered renderView="scripts" />
      )}

      <Grid container>
        <KonanPageHeader title={t("pageTitle")} />
        {isScriptsLoading ||
        (!isScriptsLoading &&
          ((scripts && scripts.pages?.length > 0) || !!searchValue || !!Object.keys(filterValue).length)) ? (
          <Fragment>
            <Grid item container my={2}>
              <KonanSubHeader
                searchValue={searchValue}
                isDebouncedInput
                setSearchValue={setSearchValue}
                sortValue={sortValue}
                useMenuFilter
                setSortValue={(value) => {
                  setSortValue(() => value as SortKey)
                }}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                filterOptions={{
                  "Script status": Object.values(StatusTextMap),
                }}
                containerType="Script"
                sortOptions={[
                  {
                    label: t("subheader.sortOptions.mostRecent"),
                    value: "Most Recent",
                  },
                  {
                    label: t("subheader.sortOptions.name"),
                    value: "Name",
                  },
                ]}
                actions={
                  canShowDetails && canAddScript
                    ? [
                        <Button size="regular" variant="primary" onClick={() => setIsScriptDialogOpen(true)}>
                          {t("subheader.actions.newScript")}
                        </Button>,
                      ]
                    : undefined
                }
              />
            </Grid>

            <Grid item xs={12}>
              {isScriptsLoading ? (
                <Grid container spacing={2}>
                  {[1, 2, 3].map((item: number) => (
                    <Grid item xs={12} md={6} lg={4} key={item}>
                      <ScriptLoadingCard />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={2} pb={1}>
                  {scripts && scripts.pages?.length > 0 ? (
                    <Fragment>
                      {scripts.pages?.map((script, index) => {
                        return (
                          <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                            key={script?.uuid}
                            ref={hasNextPage && index === scripts.pages?.length - 1 ? lastScriptRef : undefined}
                          >
                            <ScriptCard canShowDetails={canShowDetails} script={script} />
                          </Grid>
                        )
                      })}
                      {hasNextPage &&
                        [1, 2, 3].map((item: number) => (
                          <Grid item xs={12} md={6} lg={4} key={item}>
                            <ScriptLoadingCard />
                          </Grid>
                        ))}
                    </Fragment>
                  ) : Object.values(filterValue).flat(1).length !== 0 && !searchValue ? (
                    <KonanEmptyStateFilter title={Object.values(filterValue).flat(1)} />
                  ) : Object.values(filterValue).flat(1).length === 0 && !!searchValue ? (
                    <KonanEmptyState
                      title={t("emptyState.searchAndNoFilter.title", { searchValue })}
                      subTitle={t("emptyState.searchAndNoFilter.description")}
                    />
                  ) : (
                    <KonanEmptyState
                      title={t("emptyState.noSearchOrFilter.title")}
                      subTitle={t("emptyState.noSearchOrFilter.description")}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Fragment>
        ) : (
          <Grid minHeight={"50vh"} container height="100%">
            <KonanEmptyState
              title={t("emptyState.noScriptsYet.title")}
              subTitle={t("emptyState.noScriptsYet.description")}
              actionButtons={[
                <Button size="regular" variant="primary" onClick={() => setIsScriptDialogOpen(true)}>
                  {t("subheader.actions.newScript")}
                </Button>,
              ]}
            />
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}
